import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Container,
  Drawer,
  Group,
  ScrollArea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconChevronLeft,
  IconChevronRight,
  IconLogin,
  IconLogout,
} from "@tabler/icons-react";
import { authApi, safeApiCall } from "api/common.service";
import { getKnownInvoices } from "api/invoice.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import ForgotPassword from "components/auth/ForgotPassword";
import { Login } from "components/auth/Login";
import CustomerSearch from "components/header/search/CustomerSearch";
import InvoiceSearch from "components/header/search/InvoiceSearch";
import ProductSearch from "components/header/search/ProductSearch";
import SwitchSystem from "components/header/SwitchSystem";
import SwitchTheme from "components/header/SwitchTheme";
import StockSystemSideBar from "components/sidebar/admin/StockSystemSideBar";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useInvoiceStore } from "stores/useInvoiceStore";
import { useSessionStore } from "stores/userSessionStore";
import {
  FOOTER_HEIGHT,
  HEADER_HEIGHT,
  MAX_WIDTH,
  MIN_HEIGHT,
  NAVBAR_COLLAPSED_WIDTH,
  NAVBAR_WIDTH,
} from "./consts";
import FooterSocialIcons from "./FooterSocialIcons";
import classes from "./MainLayout.module.css";

const AdminLayout = ({ children }: { children: React.ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [opened, { toggle }] = useDisclosure();
  const [drawerOpened, drawer] = useDisclosure(false);
  const [mode, setMode] = useState<"login" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [loginState, setLoginState] = useState({
    username: "",
    password: "",
    rememberMe: false,
  });
  const { setInvoices, setLoading, setFetched } = useInvoiceStore();
  const location = useLocation();
  const isProductRoute = location.pathname.includes("admin/enquire-products");
  const isInvoiceRoute = location.pathname.includes("admin/invoices");
  const { isLoggedIn } = useSessionStore();
  const refreshKnownInvoices =
    location.pathname.includes("admin/payments") ||
    location.pathname.includes("admin/credit-notes") ||
    location.pathname.includes("admin/credit-journals") ||
    location.pathname.includes("admin/debit-journals");

  useEffect(() => {
    let mounted = true;

    const fetchInvoices = async () => {
      if (!refreshKnownInvoices) return;

      setLoading(true);

      const end = new Date();
      const start = new Date();
      start.setMonth(end.getMonth() - 6);

      try {
        const all = await safeApiCall(getKnownInvoices(start, end));
        if (!mounted) return;

        if (!Array.isArray(all)) {
          setLoading(false);
          return;
        }

        const seen = new Set<number>();
        const deduped = all.filter((entry) => {
          const id = Number(entry.invoiceId);
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });

        setInvoices(deduped);
        setFetched();
      } catch (err) {
        if (mounted) {
          notifications.show({
            title: "Error loading known invoices",
            message: "Could not fetch invoice details.",
            color: MantineNotificationColor.Error,
            position: MantineNotificationPosition.TopCenter,
          });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchInvoices();

    return () => {
      mounted = false;
    };
  }, [refreshKnownInvoices]);

  // Function to handle the login submission
  // This will close the drawer and set the logged-in state
  const handleLogin = async () => {
    drawer.close();
  };
  // Function to handle the forgot password action
  // This will prefill the email field with the username from the login state
  const onForgotPassword = () => {
    setEmail(loginState.username); // prefill the email field
    setMode("forgot");
  };
  // Function to handle the forgot password submission
  // This will send a reset password email to the provided email address
  const handleForgotPassword = () => {
    drawer.close();
  };
  const handleSignOut = async () => {
    await authApi.post("/users/signOut");
    useSessionStore.getState().clearUser(); // if you’re storing session info locally
  };
  return (
    <AppShell
      header={{ height: HEADER_HEIGHT }}
      footer={{ height: FOOTER_HEIGHT }}
      navbar={{
        width: collapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_WIDTH,
        breakpoint: "sm",
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header zIndex={2000}>
        <Container size={MAX_WIDTH} h="100%" px="md">
          <Group
            h="100%"
            px="md"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Group>
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="sm"
                size="sm"
              />
            </Group>

            <Group>
              {isProductRoute ? (
                <ProductSearch />
              ) : isInvoiceRoute ? (
                <InvoiceSearch />
              ) : (
                <CustomerSearch />
              )}

              {isLoggedIn ? (
                <ActionIcon variant="light" onClick={handleSignOut}>
                  <IconLogout title="Sign me out! 🛡️" size={20} />
                </ActionIcon>
              ) : (
                <ActionIcon
                  variant="light"
                  onClick={() => {
                    setMode("login");
                    drawer.open();
                  }}
                >
                  <IconLogin size={20} />
                </ActionIcon>
              )}

              <SwitchTheme />
              <SwitchSystem />
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      <AppShell.Navbar p="md" zIndex={1}>
        <Box
          w={collapsed ? NAVBAR_COLLAPSED_WIDTH : NAVBAR_WIDTH}
          style={{
            padding: collapsed ? "0.5rem" : "1rem",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            height: "100%", // fill navbar height
          }}
        >
          {/* Toggle button always visible */}
          <ActionIcon
            variant="light"
            size="lg"
            onClick={() => setCollapsed((c) => !c)}
            style={{
              position: "absolute",
              top: "-1rem",
              right: collapsed ? "0.25rem" : "1rem",
              zIndex: 2,
            }}
          >
            {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
          </ActionIcon>

          {/* Sidebar only visible when expanded */}
          {!collapsed && (
            <ScrollArea
              style={{
                marginTop: "2.5rem", // push below toggle
                flex: 1, // take remaining height
              }}
            >
              <StockSystemSideBar />
            </ScrollArea>
          )}
        </Box>
      </AppShell.Navbar>

      <AppShell.Main>
        <Container
          fluid
          size={MAX_WIDTH}
          style={{ minHeight: MIN_HEIGHT }}
          px="md"
        >
          {children}
        </Container>
      </AppShell.Main>

      <AppShell.Footer style={{ width: "100%", zIndex: 1000 }} zIndex={2000}>
        <Container className={classes.mainFooterContent}>
          <FooterSocialIcons />
          <Box
            style={{
              position: "absolute",
              bottom: "0",
              right: "1rem",
              fontSize: "0.75rem",
              color: "#888",
            }}
          >
            © {new Date().getFullYear()} John Jacobson Limited. All rights
            reserved.
          </Box>
          <Box
            style={{
              position: "absolute",
              bottom: "0",
              left: "1rem",
              fontSize: "0.75rem",
              color: "#888",
            }}
          >
            Version: {process.env.REACT_APP_BUILD_VERSION}
            {` - (${new Date().toDateString()})`}
          </Box>
        </Container>
      </AppShell.Footer>

      <Drawer
        opened={drawerOpened}
        onClose={drawer.close}
        title={mode === "login" ? "Login" : "Reset Password"}
        position="right"
        size="md"
        overlayProps={{ backgroundOpacity: 0.5, blur: 4 }}
      >
        <Box mt="xl" style={{ width: "100%" }}>
          <img
            src="/images/login2.jpg"
            alt="Drawer footer"
            style={{
              width: "100%",
              objectFit: "cover",
              borderRadius: "8px",
            }}
          />
        </Box>
        {mode === "login" ? (
          <Login
            values={loginState}
            setValues={(v) => setLoginState((prev) => ({ ...prev, ...v }))}
            onSubmit={handleLogin}
            onForgotPassword={onForgotPassword}
          />
        ) : (
          <ForgotPassword
            email={email}
            setEmail={setEmail}
            onSubmit={handleForgotPassword}
            onBack={() => setMode("login")}
          />
        )}
      </Drawer>
    </AppShell>
  );
};

export default AdminLayout;
